import type { QueryBuilder } from "@cgarciagarcia/react-query-builder";
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  cancelUserAppointment,
  CancelUserAppointmentData,
  changeRole,
  checkIntakeFormStatus,
  createUser,
  getCurrentUser,
  getUser,
  getUserAppointments,
  getUsers,
  sendMagicLink,
  updateUser,
  updateUserEmail,
  updateUserFromAdmin,
  type UserAppointment,
} from "~/api";
import { ROLES, Source, User } from "~/shared.types";
import { errorToast } from "~/ui/admin";
import { toast } from "~/ui/admin/toast/toast";

type GetUserResponse = Awaited<ReturnType<typeof getUser>>;

const ONE_MINUTE_MS = 60 * 1000;
const CURRENT_USER_STALE_MINUTES = 5;
const CURRENT_USER_STALE_TIME_MS = CURRENT_USER_STALE_MINUTES * ONE_MINUTE_MS;

export const useUsers = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const useGetUsers = ({
    queryBuilder,
  }: {
    queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
  }) => {
    return useQuery({
      queryFn: () =>
        getUsers({
          queryBuilder,
        }),
      queryKey: ["getUsers", queryBuilder?.toArray()],
    });
  };

  const useGetUser = <TData = GetUserResponse,>(
    id: User["id"],
    { select }: { select?: (data: GetUserResponse) => TData } = {},
  ) => {
    return useQuery({
      queryFn: () => getUser(id),
      queryKey: ["getUser", id],
      enabled: !!id,
      select,
    });
  };

  const useGetCurrentUser = (enabled: boolean = true) => {
    return useQuery({
      queryFn: () => getCurrentUser(),
      queryKey: ["getCurrentUser"],
      staleTime: CURRENT_USER_STALE_TIME_MS,
      enabled,
    });
  };

  const useCheckIntakeFormStatus = (id: User["id"] | null) => {
    return useQuery({
      queryFn: id ? () => checkIntakeFormStatus(id) : skipToken,
      queryKey: ["checkIntakeFormStatus", id],
      refetchOnWindowFocus: true,
      staleTime: 0,
    });
  };

  const useGetUserAppointments = (isUpcoming: boolean = true) => {
    return useQuery({
      queryFn: () => getUserAppointments({ isUpcoming }),
      queryKey: ["getUserAppointments", { isUpcoming }],
    });
  };

  const changeRoleMutation = useMutation({
    mutationFn: changeRole,
    retry: false,
    onSuccess: (res) => {
      const article = res.data.userType === ROLES.ADMIN.value ? "an" : "a";

      toast({
        type: "success",
        title: `User role updated`,
        description: `${res.data.firstName} ${res.data.lastName} is now ${article} ${res.data.userType}.`,
      });

      queryClient.invalidateQueries({
        queryKey: ["getUsers"],
      });

      navigate(-1);
    },
    onError: errorToast,
  });

  const createUserMutation = useMutation({
    mutationFn: createUser,
    retry: false,
    onSuccess: (res) => {
      toast({
        type: "success",
        title: "User created",
        description: `${res.data.firstName} ${res.data.lastName} was added. Set the role, source and test-user flag here.`,
      });

      queryClient.invalidateQueries({
        queryKey: ["getUsers"],
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
    retry: false,
    onError: errorToast,
  });

  const updateUserFromAdminMutation = useMutation({
    mutationFn: ({
      userId,
      isTest,
      source,
    }: {
      userId: string;
      isTest?: boolean;
      source?: Source;
    }) => updateUserFromAdmin(userId, { isTest, source }),
    retry: false,
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["getUser", variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUsers"],
      });
    },
    onError: errorToast,
  });

  const updateUserEmailMutation = useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      updateUserEmail(userId, email),
    retry: false,
    onSuccess: (_res, variables) => {
      toast({
        type: "success",
        title: "Email updated",
      });
      queryClient.invalidateQueries({
        queryKey: ["getUser", variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUsers"],
      });
    },
  });

  const sendMagicLinkMutation = useMutation({
    mutationFn: sendMagicLink,
    retry: false,
    onSuccess: (res) => {
      toast({
        type: "success",
        title: "Magic link sent",
        description: res.data.message,
      });
    },
    onError: errorToast,
  });

  const cancelAppointmentMutation = useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CancelUserAppointmentData;
    }) => cancelUserAppointment(appointmentId, data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getUserAppointments"],
        refetchType: "none",
      });
    },
    onError: errorToast,
  });

  const removeCanceledAppointment = (appointmentId: string) => {
    const upcomingKey = ["getUserAppointments", { isUpcoming: true }];
    const upcoming = queryClient.getQueryData<UserAppointment[]>(upcomingKey);

    queryClient.removeQueries({ queryKey: ["getUserAppointments"] });

    if (!upcoming) return;

    queryClient.setQueryData(
      upcomingKey,
      upcoming.filter((appointment) => appointment.id !== appointmentId),
    );
  };

  return {
    getUsers: useGetUsers,
    getUser: useGetUser,
    getCurrentUser: useGetCurrentUser,
    getUserAppointments: useGetUserAppointments,
    checkIntakeFormStatus: useCheckIntakeFormStatus,
    changeRoleMutation,
    createUserMutation,
    updateUserMutation,
    updateUserFromAdminMutation,
    updateUserEmailMutation,
    sendMagicLinkMutation,
    cancelAppointmentMutation,
    removeCanceledAppointment,
  };
};
